import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../../api';

export const useStudents = (tenantId) => {
  const { state } = useLocation();
  const tenantIdFromState = state?.tenantId;
  const effectiveTenantId = tenantId || tenantIdFromState;

  // --- STATE ---
  const [academicYears, setAcademicYears] = useState([]);
  const [completedAcademicYears, setCompletedAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  
  // Semester & Class Logic
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  
  // Term support for non-semester-based
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(''); // Can be 'unallocated'
  
  // Data & Status
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Loading states
  const [academicYearsLoading, setAcademicYearsLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(false);
  const [semestersLoading, setSemestersLoading] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // This filters by authStatus now
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [itemsPerPage] = useState(1000);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Merged list for display
  const [allAcademicYears, setAllAcademicYears] = useState([]);

  // --- HELPERS ---

  const isAcademicYearCompleted = useCallback((academicYear) => {
    return academicYear?.status === 'completed' || 
           academicYear?.isCompleted === true ||
           academicYear?.academicYearStatus === 'completed';
  }, []);

  const isSemesterCompleted = useCallback((semester) => {
    return semester?.status === 'completed' || 
           semester?.isCompleted === true ||
           semester?.semesterStatus === 'completed';
  }, []);

  const isTermCompleted = useCallback((term) => {
    return term?.status === 'completed' || 
           term?.isCompleted === true ||
           term?.termStatus === 'completed';
  }, []);

  const updateViewOnlyStatus = useCallback(() => {
    if (!selectedAcademicYear && academicYears.length === 0) {
      setIsViewOnly(false);
      return;
    }
    const selectedYear = [...academicYears, ...completedAcademicYears].find(y => (y.id || y._id) === selectedAcademicYear);
    
    if (selectedYear && isAcademicYearCompleted(selectedYear)) {
      setIsViewOnly(true);
      return;
    }
    
    if (isSemesterBased && selectedSemester) {
      const selectedSem = semesters.find(sem => (sem.id || sem._id) === selectedSemester);
      if (selectedSem && isSemesterCompleted(selectedSem)) {
        setIsViewOnly(true);
        return;
      }
    }
    
    if (!isSemesterBased && selectedTerm) {
      const selectedTermObj = terms.find(t => (t.id || t._id) === selectedTerm);
      if (selectedTermObj && isTermCompleted(selectedTermObj)) {
        setIsViewOnly(true);
        return;
      }
    }
    
    setIsViewOnly(false);
  }, [selectedAcademicYear, academicYears, completedAcademicYears, isSemesterBased, selectedSemester, semesters, selectedTerm, terms, isAcademicYearCompleted, isSemesterCompleted, isTermCompleted]);

  // --- API FETCHERS ---

  const fetchAcademicYears = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    setAcademicYearsLoading(true);
    setError(null);
    
    try {
      const [activeRes, completedRes] = await Promise.all([
        api.get(`/api/academic-year/all-active?tenantId=${effectiveTenantId}`),
        api.get(`/api/academic-year/all-completed?tenantId=${effectiveTenantId}`)
      ]);

      const activeYears = activeRes.data.academicYears || [];
      const completedYears = completedRes.data.academicYears || [];

      setAcademicYears(activeYears);
      setCompletedAcademicYears(completedYears);

      setAllAcademicYears([
        ...activeYears.map(y => ({ ...y, isCompleted: false })),
        ...completedYears.map(y => ({ ...y, isCompleted: true }))
      ]);

      // Auto-select logic
      if (activeYears.length > 0) {
        const firstYear = activeYears[0];
        setSelectedAcademicYear(firstYear.id || firstYear._id);
        setIsSemesterBased(firstYear.isSemesterBased || false);
      } else if (completedYears.length > 0) {
        setSelectedAcademicYear(completedYears[0].id || completedYears[0]._id);
        setIsSemesterBased(completedYears[0].isSemesterBased || false);
        setIsViewOnly(true);
        setError('No active academic years found. Viewing completed years.');
      } else {
        setSelectedAcademicYear('');
        setError('No academic years found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching academic years');
    } finally {
      setAcademicYearsLoading(false);
    }
  }, [effectiveTenantId]);

  const fetchSemesters = useCallback(async () => {
    if (!selectedAcademicYear) {
      setSemesters([]);
      return;
    }
    
    setSemestersLoading(true);
    try {
      const response = await api.get(`/api/classManager/semesters/${selectedAcademicYear}`);
      const allSemesters = response.data.semesters || [];
      setSemesters(allSemesters);

      if (allSemesters.length > 0 && !selectedSemester) {
        setSelectedSemester(allSemesters[0].id || allSemesters[0]._id);
      } else if (allSemesters.length === 0) {
        setSelectedSemester('');
        setClasses([]);
        setSelectedClass('');
      }
    } catch (err) {
      console.warn('Error fetching semesters:', err);
      setSemesters([]);
    } finally {
      setSemestersLoading(false);
    }
  }, [selectedAcademicYear, selectedSemester]);

  const fetchTerms = useCallback(async () => {
    if (!selectedAcademicYear) {
      setTerms([]);
      return;
    }
    
    setTermsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/academic-year/${selectedAcademicYear}/terms?tenantId=${effectiveTenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allTerms = response.data.terms || [];
      setTerms(allTerms);

      if (allTerms.length > 0 && !selectedTerm) {
        setSelectedTerm(allTerms[0].id || allTerms[0]._id);
      } else if (allTerms.length === 0) {
        setSelectedTerm('');
        setClasses([]);
        setSelectedClass('');
      }
    } catch (err) {
      console.warn('Error fetching terms:', err);
      setTerms([]);
    } finally {
      setTermsLoading(false);
    }
  }, [selectedAcademicYear, effectiveTenantId, selectedTerm]);

  const fetchClasses = useCallback(async () => {
    if (!selectedAcademicYear) {
      setClasses([]);
      setClassesLoading(false);
      return;
    }

    setClassesLoading(true);
    try {
      let response;
      let classesData = [];
      
      if (isSemesterBased) {
        if (!selectedSemester) {
          setClasses([]);
          setClassesLoading(false);
          return;
        }
        response = await api.get(`/api/classManager/classes-for-semester/${selectedSemester}`);
        classesData = response.data?.classes || response.data || [];
      } else {
        if (selectedTerm) {
          response = await api.get(`/api/classManager/classes-for-term/${selectedTerm}`);
          classesData = response.data?.classes || response.data || [];
        } else {
          response = await api.get(`/api/classManager/classes-for-academic-year/${selectedAcademicYear}?includeAnnual=true`);
          classesData = response.data?.classes || response.data || [];
          classesData = classesData.filter(cls => !cls.termId && !cls.semesterId);
        }
      }

      setClasses(Array.isArray(classesData) ? classesData : []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, [isSemesterBased, selectedSemester, selectedTerm, selectedAcademicYear]);

  // Main Fetch Logic - FIXED: Now passes statusFilter correctly
  const fetchAllStudentsForTenant = useCallback(async (page = 1, filters = {}) => {
    if (!effectiveTenantId || !selectedAcademicYear) {
      console.log('⚠️ Cannot fetch students: missing tenantId or academicYear');
      return;
    }
    
    setStudentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        tenantId: effectiveTenantId,
        academicYearId: selectedAcademicYear,
        page: page.toString(),
        limit: itemsPerPage.toString(),
        ...filters
      };

      if (isSemesterBased && selectedSemester) {
        queryParams.semesterId = selectedSemester;
      }
      
      if (!isSemesterBased && selectedTerm) {
        queryParams.termId = selectedTerm;
      }

      // FIX: Convert statusFilter to backend expected format
      if (statusFilter) {
        queryParams.status = statusFilter; // Backend will use this to filter AuthUser tenantRoles
      }

      const params = new URLSearchParams(queryParams);
      console.log(`📊 Fetching students with params:`, Object.fromEntries(params));

      const response = await api.get(`/api/students/get?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsData = response.data.students || [];
      console.log(`✅ Received ${studentsData.length} students from API`);
      
      setStudents(studentsData);
      setCurrentPage(page);
      setTotalPages(response.data.totalPages || 1);
      setTotalStudents(response.data.totalStudents || 0);
      
      return response.data;
    } catch (err) {
      console.error('❌ Error fetching students:', err);
      setStudents([]);
      setError(err.response?.data?.message || "Error fetching students");
    } finally {
      setStudentsLoading(false);
    }
  }, [effectiveTenantId, selectedAcademicYear, itemsPerPage, isSemesterBased, selectedSemester, selectedTerm, statusFilter]);

  // Public Refresh Method
  const refreshStudents = useCallback(async (page = 1) => {
    const filters = {
      ...(selectedClass && { classId: selectedClass }),
      ...(searchQuery && { search: searchQuery })
      // Note: statusFilter is now handled separately in fetchAllStudentsForTenant
    };
    await fetchAllStudentsForTenant(page, filters);
  }, [fetchAllStudentsForTenant, selectedClass, searchQuery]);

  // --- HANDLERS ---

  const handleAcademicYearChange = (academicYearId) => {
    setSelectedAcademicYear(academicYearId);
    setSelectedSemester('');
    setSelectedTerm('');
    setSelectedClass('');
    setSearchQuery('');
    setStatusFilter('');
    setCurrentPage(1);
    
    const allYears = [...academicYears, ...completedAcademicYears];
    const yearObj = allYears.find(y => (y.id || y._id) === academicYearId);
    
    const isSem = yearObj?.isSemesterBased || false;
    setIsSemesterBased(isSem);
    setIsViewOnly(isAcademicYearCompleted(yearObj));
    
    setSemesters([]);
    setTerms([]);
    setClasses([]);
  };

  const handleSemesterChange = (semesterId) => {
    setSelectedSemester(semesterId);
    setSelectedTerm('');
    setSelectedClass('');
    setCurrentPage(1);
  };

  const handleTermChange = (termId) => {
    setSelectedTerm(termId);
    setSelectedSemester('');
    setSelectedClass('');
    setCurrentPage(1);
  };

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    setCurrentPage(1);
  };

  const handleSearch = useCallback(async () => {
    await refreshStudents(1);
  }, [refreshStudents]);

  // --- EFFECTS ---

  useEffect(() => {
    const fetchInit = async () => {
      if (!effectiveTenantId) {
        setError('Tenant ID is required');
        setLoading(false);
        return;
      }
      await fetchAcademicYears();
      setLoading(false);
    };
    fetchInit();
  }, [effectiveTenantId, fetchAcademicYears]);

  useEffect(() => {
    updateViewOnlyStatus();
  }, [updateViewOnlyStatus]);

  useEffect(() => {
    if (selectedAcademicYear) {
      if (isSemesterBased) {
        fetchSemesters();
        if (selectedSemester) fetchClasses();
      } else {
        fetchTerms();
        if (selectedTerm) fetchClasses();
        else fetchClasses();
      }
    }
  }, [selectedAcademicYear, isSemesterBased, selectedSemester, selectedTerm, fetchSemesters, fetchTerms, fetchClasses]);

  // FIX: Auto-Fetch Students when filters change
  useEffect(() => {
    if (selectedAcademicYear) {
      // Wait for required filters based on context
      if (isSemesterBased && !selectedSemester) {
        console.log('⏳ Waiting for semester selection...');
        return;
      }
      
      const filters = {
        ...(selectedClass && { classId: selectedClass }),
        ...(searchQuery && { search: searchQuery })
      };
      
      console.log('🔄 Auto-fetching students with filters:', { 
        statusFilter, 
        selectedClass, 
        searchQuery,
        isSemesterBased,
        selectedSemester,
        selectedTerm
      });
      
      fetchAllStudentsForTenant(1, filters);
    }
  }, [selectedClass, statusFilter, selectedAcademicYear, selectedSemester, selectedTerm, isSemesterBased, fetchAllStudentsForTenant, searchQuery]);

  return {
    // Data
    academicYears,
    completedAcademicYears,
    allAcademicYears,
    semesters,
    terms,
    classes,
    students,
    
    // Status
    loading,
    studentsLoading,
    error,
    isViewOnly,
    
    // Loading states
    academicYearsLoading,
    classesLoading,
    semestersLoading,
    termsLoading,
    
    // Selection
    selectedAcademicYear,
    isSemesterBased,
    selectedSemester,
    selectedTerm,
    selectedClass,
    searchQuery,
    statusFilter,
    
    // Pagination
    totalStudents,
    currentPage,
    totalPages,
    itemsPerPage,
    
    // Actions
    handleAcademicYearChange,
    handleSemesterChange,
    handleTermChange,
    handleClassChange,
    handleSearch,
    setSearchQuery,
    setStatusFilter,
    setSelectedClass,
    setCurrentPage,
    fetchAllStudentsForTenant,
    refreshStudents
  };
};